'use client'
import React, { useImperativeHandle, useRef, useState, useEffect } from 'react'
import { Heading, Picture, Text, Link } from '../../components/index.js'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import { useIntersectionObserverDelay } from '../../hooks/useIntersectionObserver.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { motion, AnimatePresence } from 'framer-motion'
import type { ImageWithTextProps, ImageWithTextItem, ImageWithTextTabItem } from './types.js'

const componentType = 'image'
const componentName = 'image_with_text'

const ArrowRight = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M6.91009 4.41058C7.23553 4.08514 7.76304 4.08514 8.08848 4.41058L13.0885 9.41058C13.4139 9.73602 13.4139 10.2635 13.0885 10.589L8.08848 15.589C7.76304 15.9144 7.23553 15.9144 6.91009 15.589C6.58466 15.2635 6.58466 14.736 6.91009 14.4106L11.3209 9.99977L6.91009 5.58897C6.58466 5.26353 6.58466 4.73602 6.91009 4.41058Z"
      fill="currentColor"
    />
  </svg>
)

const ImageWithText = React.forwardRef<HTMLDivElement, ImageWithTextProps>(({ data, className }, ref) => {
  const {
    title,
    subtitle,
    desc,
    descIcon,
    image,
    padImage,
    mobileImage,
    theme = 'dark',
    items = [],
    layout = 'left',
    mediaType = 'image',
    datalist = [],
    video,
    padVideo,
    mobVideo,
    button,
    textAlign = 'left',
  } = data

  const effectiveLayout = layout

  const { locale = 'us' } = useAiuiContext()
  const boxRef = useRef<HTMLDivElement>(null)
  const desktopVideoRef = useRef<HTMLDivElement>(null)
  const tabletVideoRef = useRef<HTMLDivElement>(null)
  const mobileVideoRef = useRef<HTMLDivElement>(null)
  const mediaWrapperRef = useRef<HTMLDivElement>(null)

  // Tab状态管理
  const [activeIndex, setActiveIndex] = useState(0)
  const tabRefs = useRef<Array<HTMLDivElement | null>>([])
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 })

  // 视频懒加载状态
  const [loadedDesktopVideoSrc, setLoadedDesktopVideoSrc] = useState('')
  const [loadedTabletVideoSrc, setLoadedTabletVideoSrc] = useState('')
  const [loadedMobileVideoSrc, setLoadedMobileVideoSrc] = useState('')

  // 判断是否为TabWithImage模式（优先级最高）
  const isTabMode = datalist.length > 0

  // 判断当前是否为视频模式
  const isVideo = mediaType === 'video'

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
    componentDescription: subtitle,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  // Tab滑块位置计算
  useEffect(() => {
    if (datalist.length > 0) {
      const current = tabRefs.current[activeIndex]
      if (current) {
        const { offsetLeft, offsetWidth } = current
        setSliderStyle({ left: offsetLeft, width: offsetWidth })
      }
    }
  }, [activeIndex, datalist.length])

  // 视频懒加载逻辑（使用 useEffect 避免闭包问题）
  const [videoIntersected, setVideoIntersected] = useState(false)

  // 使用 IntersectionObserver 检测媒体区域是否可见（监听父容器避免响应式隐藏问题）
  useIntersectionObserverDelay(mediaWrapperRef, {
    once: true,
    threshold: 0.01,
    callback: () => {
      setVideoIntersected(true)
    },
  })

  // 当视频区域可见时，加载对应的视频源
  useEffect(() => {
    if (!videoIntersected || !isVideo) return

    // 确定视频源
    let desktopSrc = ''
    let tabletSrc = ''
    let mobileSrc = ''

    if (isTabMode && datalist[activeIndex]) {
      const activeTab = datalist[activeIndex]
      desktopSrc = activeTab.video?.url || ''
      tabletSrc = activeTab.padVideo?.url || desktopSrc
      mobileSrc = activeTab.mobVideo?.url || desktopSrc
    } else {
      desktopSrc = video?.url || ''
      tabletSrc = padVideo?.url || desktopSrc
      mobileSrc = mobVideo?.url || desktopSrc
    }

    // 设置视频源
    if (desktopSrc) setLoadedDesktopVideoSrc(desktopSrc)
    if (tabletSrc) setLoadedTabletVideoSrc(tabletSrc)
    if (mobileSrc) setLoadedMobileVideoSrc(mobileSrc)

    // 延迟触发播放
    setTimeout(() => {
      const videos = [
        desktopVideoRef.current?.querySelector('video'),
        tabletVideoRef.current?.querySelector('video'),
        mobileVideoRef.current?.querySelector('video'),
      ]

      videos.forEach(videoElement => {
        if (videoElement) {
          videoElement.load()
          videoElement.play().catch(() => {
            // 静默处理自动播放失败的情况
          })
        }
      })
    }, 200)
  }, [videoIntersected, isVideo, isTabMode, activeIndex, datalist, video?.url, padVideo?.url, mobVideo?.url])

  const handleTabClick = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
    setActiveIndex(index)
    ;(e.target as HTMLElement).scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })

    // 如果是视频模式并且切换了Tab，需要重新加载和播放视频
    if (isVideo && isTabMode && datalist[index]) {
      const activeTab = datalist[index]

      // 更新视频源
      if (activeTab.video?.url) {
        setLoadedDesktopVideoSrc(activeTab.video.url)
      }
      if (activeTab.padVideo?.url) {
        setLoadedTabletVideoSrc(activeTab.padVideo.url)
      }
      if (activeTab.mobVideo?.url) {
        setLoadedMobileVideoSrc(activeTab.mobVideo.url)
      }

      // 延迟触发播放，确保DOM更新完成
      setTimeout(() => {
        const videos = [
          desktopVideoRef.current?.querySelector('video'),
          tabletVideoRef.current?.querySelector('video'),
          mobileVideoRef.current?.querySelector('video'),
        ]

        videos.forEach(video => {
          if (video) {
            video.load()
            video.play().catch(() => {
              // 静默处理自动播放失败的情况
            })
          }
        })
      }, 300) // 稍微延长时间以确保framer-motion动画完成
    }
  }

  // 渲染按钮
  const renderButton = () => {
    if (!hasButton || !button?.text) return null
    return (
      <div
        className={cn('image-with-text__button-wrapper laptop:mt-[32px] mt-[24px]', {
          'flex justify-center': textAlign === 'center',
          'flex justify-start': textAlign === 'left',
          'laptop:hidden': effectiveLayout === 'top' || effectiveLayout === 'bottom',
        })}
      >
        <Link
          href={getLocalizedPath(button.link || '', locale)}
          className="image-with-text__button whitespace-nowrap no-underline"
          suffixIcon={ArrowRight}
        >
          {button.text}
        </Link>
      </div>
    )
  }

  // 判断是否有功能列表
  const hasItems = items.length > 0

  // 判断是否有按钮（影响文本区域的对齐方式）
  const hasButton = Boolean(button)

  // 获取图片源
  const getImageSource = () => {
    if (isTabMode && datalist[activeIndex]) {
      const activeTab = datalist[activeIndex]
      return `${activeTab.image?.url} ,${activeTab.imgPad?.url || activeTab.image?.url} 1440, ${activeTab.imageMob?.url || activeTab.image?.url} 767`
    }
    if (image) {
      return `${image?.url},${padImage?.url || image?.url} 1024, ${mobileImage?.url || image?.url} 768`
    }
    return ''
  }

  // 渲染视频内容（支持三端）
  const renderVideo = () => {
    if (isTabMode) {
      const activeTab = datalist[activeIndex]
      // Tab模式下的视频，使用 image/imgPad/imageMob 作为封面
      const desktopPoster = activeTab.image?.url
      const tabletPoster = activeTab.imgPad?.url || desktopPoster
      const mobilePoster = activeTab.imageMob?.url || desktopPoster

      return (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab.video?.url || activeTab.image?.url}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="image-with-text__video-motion absolute left-0 top-0 w-full"
          >
            <div className="image-with-text__video-wrapper rounded-box overflow-hidden">
              {/* 桌面端视频 */}
              <div ref={desktopVideoRef} className="image-with-text__desktop-video-container">
                <video
                  playsInline
                  autoPlay
                  muted
                  poster={desktopPoster}
                  src={loadedDesktopVideoSrc}
                  loop
                  className="image-with-text__video image-with-text__video--desktop lg-desktop:block hidden size-full object-cover"
                ></video>
              </div>
              {/* 平板端视频 */}
              <div ref={tabletVideoRef} className="image-with-text__tablet-video-container">
                <video
                  playsInline
                  autoPlay
                  muted
                  poster={tabletPoster}
                  src={loadedTabletVideoSrc || loadedDesktopVideoSrc}
                  loop
                  className="image-with-text__video image-with-text__video--tablet tablet:block lg-desktop:hidden hidden"
                ></video>
              </div>
              {/* 移动端视频 */}
              <div ref={mobileVideoRef} className="image-with-text__mobile-video-container">
                <video
                  playsInline
                  autoPlay
                  muted
                  poster={mobilePoster}
                  src={loadedMobileVideoSrc || loadedDesktopVideoSrc}
                  loop
                  className="image-with-text__video image-with-text__video--mobile tablet:hidden block"
                ></video>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      )
    }

    // 基础模式下的视频（支持三端），使用 image/padImage/mobileImage 作为封面
    const desktopPoster = image?.url
    const tabletPoster = padImage?.url || desktopPoster
    const mobilePoster = mobileImage?.url || desktopPoster

    return (
      <div className="image-with-text__video-wrapper rounded-box laptop:rounded-box overflow-hidden">
        {/* 桌面端视频 */}
        <div ref={desktopVideoRef} className="image-with-text__desktop-video-container">
          <video
            playsInline
            autoPlay
            muted
            poster={desktopPoster}
            src={loadedDesktopVideoSrc}
            loop
            className="image-with-text__video image-with-text__video--desktop lg-desktop:block hidden size-full object-cover"
          ></video>
        </div>
        {/* 平板端视频 */}
        <div ref={tabletVideoRef} className="image-with-text__tablet-video-container">
          <video
            playsInline
            autoPlay
            muted
            poster={tabletPoster}
            src={loadedTabletVideoSrc || loadedDesktopVideoSrc}
            loop
            className="image-with-text__video image-with-text__video--tablet tablet:block lg-desktop:hidden hidden"
          ></video>
        </div>
        {/* 移动端视频 */}
        <div ref={mobileVideoRef} className="image-with-text__mobile-video-container">
          <video
            playsInline
            autoPlay
            muted
            poster={mobilePoster}
            src={loadedMobileVideoSrc || loadedDesktopVideoSrc}
            loop
            className="image-with-text__video image-with-text__video--mobile tablet:hidden block"
          ></video>
        </div>
      </div>
    )
  }

  return (
    <section
      ref={boxRef}
      data-ui-component-id="ImageWithText"
      className={cn(
        'image-with-text text-info-primary',
        {
          // 基础模式样式 - 支持上下左右布局
          'flex gap-[24px] laptop:gap-[48px] lg-desktop:gap-[64px]': !isTabMode,
          'flex-col': !isTabMode && (effectiveLayout === 'top' || effectiveLayout === 'bottom'),
          'items-center': effectiveLayout === 'left' || effectiveLayout === 'right',
          'flex-col laptop:flex-row': !isTabMode && (effectiveLayout === 'left' || effectiveLayout === 'right'),
          // TabWithImage模式样式
          'flex l:gap-[24px] xl:flex-col min-md:justify-between min-l:gap-[20px]': isTabMode,
          // 主题样式
          'aiui-dark': theme === 'dark',
        },
        className
      )}
    >
      {/* TabWithImage模式的内容和Tab控制 */}
      {isTabMode && (title || desc || datalist.length > 0) && (
        <div
          className={cn(
            'image-with-text__tab-content min-md:gap-[24px] laptop:basis-[36%] inline-flex flex-col justify-center',
            {
              'text-left': textAlign === 'left',
              'text-center': textAlign === 'center',
            }
          )}
        >
          <div className="image-with-text__header">
            <Heading as={'h3'} size={4} html={title} className="image-with-text__title" />
            <Text
              as={'p'}
              size={1}
              html={desc}
              className="image-with-text__description tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] text-[14px]"
            />
          </div>

          <div
            className={cn(
              'image-with-text__tabs-wrapper md:scrollbar-hidden relative md:overflow-hidden md:overflow-x-scroll',
              {
                'flex justify-center': textAlign === 'center',
              }
            )}
          >
            <div className="image-with-text__tabs rounded-btn relative inline-flex bg-[#1D1D1F] px-[4px] md:my-[24px]">
              {/* 滑动背景 */}
              <div
                className="image-with-text__slider rounded-btn absolute inset-y-0 bg-white transition-all duration-300 ease-in-out"
                style={{
                  left: sliderStyle.left,
                  width: sliderStyle.width,
                }}
              />

              {/* Tab Items */}
              {datalist.map((item: ImageWithTextTabItem, index: number) => (
                <div
                  key={index}
                  ref={el => {
                    tabRefs.current[index] = el
                  }}
                  onClick={e => handleTabClick(index, e)}
                  className={cn(
                    'image-with-text__tab rounded-btn min-xxl:px-[28px] min-xxl:py-[15px] relative z-10 cursor-pointer px-[20px] py-[10px] text-center text-[12px] font-medium transition-colors duration-300 md:px-[20px] md:py-[10px]',
                    activeIndex === index ? 'image-with-text__tab--active text-black' : 'text-white'
                  )}
                >
                  <Heading
                    as="h1"
                    size={1}
                    html={item?.title}
                    className="image-with-text__tab-title text-balance-normal !whitespace-nowrap md:text-[14px]"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 基础模式的内容区域 */}
      {!isTabMode && (title || subtitle || desc) && (
        <div
          className={cn('image-with-text__content flex flex-col', {
            // 垂直对齐：如果有按钮则使用 justify-between，否则居中对齐
            'justify-center': !hasButton,
            'justify-between': hasButton,
            // 使用items时的特殊样式（类似原Feature模式）
            'w-full laptop:w-fit': hasItems,
            // 水平对齐控制：当有功能列表时，根据textAlign决定对齐方式
            'items-start': textAlign === 'left',
            'items-center': textAlign === 'center',
            // 文本对齐控制
            'text-left': textAlign === 'left',
            'text-center': textAlign === 'center',
            // order控制
            'laptop:order-1': effectiveLayout === 'left',
          })}
        >
          {/* 主要内容区域（当有按钮时，这个div包含除按钮外的所有内容） */}
          <div className={cn('image-with-text__main-content', { 'flex flex-col': hasButton })}>
            <div
              className={cn('image-with-text__header', {
                'laptop:flex laptop:items-end laptop:justify-between laptop:gap-16':
                  (effectiveLayout === 'top' || effectiveLayout === 'bottom') && button?.text,
              })}
            >
              <div className="image-with-text__title-wrapper flex flex-col">
                <Heading
                  as={'h2'}
                  size={4}
                  html={title}
                  className={cn('image-with-text__title', {
                    'w-full': hasItems,
                    'text-left': hasItems && textAlign === 'left',
                    'text-center': hasItems && textAlign === 'center',
                  })}
                />
                {subtitle && (
                  <Text
                    as={'p'}
                    size={hasItems ? 4 : 3}
                    html={subtitle}
                    className={cn(
                      'image-with-text__subtitle tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] text-[14px]',
                      {
                        'laptop:mt-[16px]': !hasItems,
                        'min-xxl:mt-[8px] laptop:text-[16px] lg-desktop:text-[18px]': hasItems,
                        'text-left': hasItems && textAlign === 'left',
                        'text-center': hasItems && textAlign === 'center',
                      }
                    )}
                  />
                )}
              </div>
              {(effectiveLayout === 'top' || effectiveLayout === 'bottom') && button?.text && (
                <Link
                  href={getLocalizedPath(button.link || '', locale)}
                  className="laptop:flex hidden whitespace-nowrap no-underline"
                  suffixIcon={ArrowRight}
                >
                  {button.text}
                </Link>
              )}
            </div>

            {/* 基础模式的描述（不使用items时） */}
            {!hasItems && (desc || descIcon) && (
              <div className="image-with-text__description flex flex-row gap-[8px]">
                {descIcon && (
                  <img
                    src={descIcon}
                    alt="icon"
                    className="image-with-text__description-icon desktop:size-[48px] size-[36px]"
                  />
                )}
                {desc && (
                  <Heading
                    as={'h4'}
                    size={5}
                    html={desc}
                    className="image-with-text__description-text min-md:text-[40px] min-l:text-[40px] min-xl:text-[56px] min-xxl:text-[64px] text-[#3AD1FF] md:text-[40px]"
                  />
                )}
              </div>
            )}

            {/* 功能列表（当有items时显示） */}
            {hasItems && (
              <div
                className={cn('image-with-text__items laptop:mt-[32px] desktop:mt-[48px] mt-[24px] grid w-full gap-6', {
                  '!mt-6': effectiveLayout === 'top' || effectiveLayout === 'bottom',
                  // 当 effectiveLayout 是 top 或 bottom 时：默认 cols 是 2，tablet 或以上尺寸是 4
                  'grid-cols-2 tablet:grid-cols-4': effectiveLayout === 'top' || effectiveLayout === 'bottom',
                  // 当 effectiveLayout 是 left 或 right 时：cols 默认是 2，tablet 是 4，laptop 或以上尺寸是 2
                  'grid-cols-2 tablet:grid-cols-4 laptop:grid-cols-2':
                    effectiveLayout === 'left' || effectiveLayout === 'right',
                })}
              >
                {items.map((item: ImageWithTextItem, index: number) => (
                  <div
                    key={index}
                    className={cn('image-with-text__item', {
                      'text-center': textAlign === 'center',
                    })}
                  >
                    <div
                      className={cn('image-with-text__item-header flex flex-row items-center gap-[8px]', {
                        'justify-center': textAlign === 'center',
                        'justify-start': textAlign === 'left',
                      })}
                    >
                      <Picture
                        source={item.icon?.url}
                        alt={item.icon?.alt}
                        className="image-with-text__item-icon min-md:text-[40px] min-l:text-[40px] min-xl:text-[56px] min-xxl:text-[64px] desktop:h-[44px] lg-desktop:h-[52px] h-[28px] translate-y-[-12%] md:text-[40px]"
                        imgClassName="h-full !w-full"
                      />
                      <Heading
                        size={4}
                        as="h6"
                        className="image-with-text__item-text bg-gradient-to-r from-[#3ad1ff] to-[#008cd6] bg-clip-text text-transparent"
                      >
                        {item.text}
                      </Heading>
                    </div>
                    <Text
                      size={4}
                      as="p"
                      html={item.desc}
                      className="image-with-text__item-desc tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] mt-[-2px] text-[14px]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 按钮（放在主要内容区域外） */}
          {renderButton()}
        </div>
      )}

      {/* 图片/视频区域 */}
      <div
        ref={mediaWrapperRef}
        className={cn('image-with-text__media-wrapper', {
          'laptop:w-[60%] w-full shrink-0': effectiveLayout === 'left' || effectiveLayout === 'right',
          // 使用items时的特殊样式（类似原Feature模式）
          'aspect-[716/720] max-h-[560px] max-w-[824px] tablet:aspect-[704/360] laptop:aspect-[824/560] shrink-0':
            hasItems,
          'laptop:basis-[63%] desktop:basis-[57%]': hasItems && effectiveLayout === 'left',
          // TabWithImage模式
          'relative w-full shrink md:aspect-[358/360] min-xxl:aspect-[824/560] min-xxl:max-w-[824px] tablet:aspect-[704/360] laptop:aspect-[744/336] laptop:basis-[64%] desktop:aspect-[648/448]':
            isTabMode,
          '!max-w-none !max-h-none aspect-auto': effectiveLayout === 'top' || effectiveLayout === 'bottom',
        })}
      >
        {isTabMode ? (
          // TabWithImage模式 - 支持图片和视频
          isVideo ? (
            renderVideo()
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={datalist[activeIndex].image?.url}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="image-with-text__image-motion absolute left-0 top-0 w-full"
              >
                <Picture
                  source={getImageSource()}
                  alt={datalist[activeIndex].image?.alt}
                  className="image-with-text__image rounded-box min-xxl:aspect-[824/560] min-xxl:max-w-[824px] tablet:aspect-[704/360] laptop:aspect-[744/336] desktop:aspect-[648/448] md:aspect-[358/360]"
                />
              </motion.div>
            </AnimatePresence>
          )
        ) : isVideo ? (
          // 基础模式 - 视频
          renderVideo()
        ) : (
          // 基础模式 - 图片
          <Picture source={getImageSource()} className={cn('image-with-text__image rounded-box laptop:rounded-box')} />
        )}
      </div>
    </section>
  )
})

ImageWithText.displayName = 'ImageWithText'

export default withLayout(ImageWithText)
